import React, { forwardRef, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

const formatTime = (sec) => {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${ss.toString().padStart(2, '0')}`;
};

// Standalone <video> wrapper that hides the trimmed portion from the viewer.
// Native <video controls> always reports the full file duration on the
// scrubber, which leaks the cut tail/head. This component suppresses the
// native controls and renders its own scrubber over the window
// [trimStart, trimEnd] so to the student/teacher it looks like a video of
// length (trimEnd - trimStart).
//
// forwardRef exposes the inner <video> so classroom sync hooks can still
// read currentTime and fire programmatic play/pause.
const TrimmedVideoPlayer = forwardRef(function TrimmedVideoPlayer({
  src,
  trimStart = 0,
  trimEnd = null,
  muted = false,
  loop = false,
  autoPlay = false,
  className = '',
  videoClassName = 'w-full h-full object-cover',
  videoProps = {},
  showControls = true,
}, forwardedRef) {
  const internalRef = useRef(null);
  const setRefs = (node) => {
    internalRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  const safeStart = Math.max(0, Number(trimStart) || 0);
  const trimEndNum = Number(trimEnd);
  const safeEnd = Number.isFinite(trimEndNum) && trimEndNum > safeStart ? trimEndNum : null;

  const [isPlaying, setIsPlaying] = useState(false);
  const [windowTime, setWindowTime] = useState(0);
  const [windowDuration, setWindowDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(!!muted);

  const srcWithStart = safeStart > 0 ? `${src}#t=${safeStart}` : src;

  const {
    onLoadedMetadata: extLoadedMetadata,
    onTimeUpdate: extTimeUpdate,
    onPlay: extPlay,
    onPause: extPause,
    ...restVideoProps
  } = videoProps;

  const togglePlay = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const v = internalRef.current;
    if (!v) return;
    if (v.paused) {
      const endBound = safeEnd ?? v.duration ?? Infinity;
      if (v.currentTime < safeStart - 0.01 || v.currentTime >= endBound - 0.01) {
        try { v.currentTime = safeStart; } catch {}
      }
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  };

  const toggleMute = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const v = internalRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const handleScrub = (e) => {
    const v = internalRef.current;
    if (!v) return;
    const win = parseFloat(e.target.value);
    if (!Number.isFinite(win)) return;
    const clamped = Math.max(0, Math.min(windowDuration || 0, win));
    try { v.currentTime = safeStart + clamped; } catch {}
    setWindowTime(clamped);
  };

  const handleLoadedMetadata = (e) => {
    const v = e.currentTarget;
    const end = safeEnd ?? v.duration ?? 0;
    setWindowDuration(Math.max(0, end - safeStart));
    if (safeStart > 0) { try { v.currentTime = safeStart; } catch {} }
    extLoadedMetadata?.(e);
  };

  const handleTimeUpdate = (e) => {
    const v = e.currentTarget;
    const end = safeEnd ?? v.duration ?? 0;
    if (end > 0 && v.currentTime >= end - 0.03) {
      if (loop) {
        try { v.currentTime = safeStart; } catch {}
      } else {
        v.pause();
        try { v.currentTime = end; } catch {}
      }
    }
    setWindowTime(Math.max(0, v.currentTime - safeStart));
    extTimeUpdate?.(e);
  };

  const handlePlay = (e) => {
    setIsPlaying(true);
    extPlay?.(e);
  };
  const handlePause = (e) => {
    setIsPlaying(false);
    extPause?.(e);
  };

  return (
    <div className={`relative w-full h-full ${className}`}>
      <video
        ref={setRefs}
        src={srcWithStart}
        className={videoClassName}
        muted={isMuted}
        loop={loop}
        autoPlay={autoPlay}
        playsInline
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onPlay={handlePlay}
        onPause={handlePause}
        onContextMenu={(e) => e.preventDefault()}
        {...restVideoProps}
      />
      {showControls && !isPlaying && (
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/10 transition group"
          aria-label="Play"
        >
          <span className="w-14 h-14 rounded-full bg-black/50 group-hover:bg-black/70 flex items-center justify-center">
            <Play size={24} className="text-white ml-1" fill="white" />
          </span>
        </button>
      )}
      {showControls && (
        <div
          className="absolute inset-x-0 bottom-0 px-2 pb-1 pt-6 bg-gradient-to-t from-black/70 to-transparent flex items-center gap-2 text-white text-[11px] opacity-0 hover:opacity-100 focus-within:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={togglePlay}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 shrink-0"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause size={14} fill="white" /> : <Play size={14} fill="white" className="ml-0.5" />}
          </button>
          <span className="tabular-nums w-10 text-right shrink-0">{formatTime(windowTime)}</span>
          <input
            type="range"
            min={0}
            max={windowDuration || 0}
            step={0.1}
            value={Math.min(windowTime, windowDuration || 0)}
            onChange={handleScrub}
            className="flex-1 accent-white"
            aria-label="Seek"
          />
          <span className="tabular-nums w-10 shrink-0">{formatTime(windowDuration)}</span>
          <button
            type="button"
            onClick={toggleMute}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 shrink-0"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          </button>
        </div>
      )}
    </div>
  );
});

export default TrimmedVideoPlayer;
